import { AnimatePresence, motion } from 'framer-motion';
import { Box } from '~/commonComponents/Box';
import Checkbox from '~/commonComponents/Checkbox';
import { Column } from '~/commonComponents/Column';
import { Row } from '~/commonComponents/Row';
import { Text } from '~/commonComponents/Text';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IFavoriteInvite, IJob } from '~/models';
import { Colors } from '~/themes/colors';
import { Icon } from '~/commonComponents/Icon';

interface IProps {
  id: string;
  fullName: string;
  isAvailable: boolean;
  isFavorite: boolean;
  isSelected: boolean;
  profileImage: string;
  profession?: string;
  onItemClick: (id: string) => void;
}

export const ListItem = ({
  fullName,
  id,
  isAvailable,
  isFavorite,
  isSelected,
  profession,
  profileImage,
  onItemClick,
}: IProps) => {
  const isInviteSent = useAppSelector((state) =>
    (state.job.job as IJob)?.favoriteInvites?.some(
      (it: IFavoriteInvite) => (it?.professional as any) === id,
    ),
  );

  return (
    <AnimatePresence>
      <motion.div
        role="presentation"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        layout
        onClick={() => onItemClick(id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0px',
          borderBottom: `1px solid ${Colors.neutral_100}`,
          cursor: isInviteSent ? 'auto' : 'pointer',
        }}
      >
        <Row alignItems="center" gap={12} width="100%">
          <Box
            border={`2px solid ${isFavorite ? Colors.favorites_400 : 'transparent'}`}
            borderRadius="100%"
            height={64}
            padding={2}
            position="relative"
            width={64}
          >
            <img
              src={profileImage}
              style={{
                borderRadius: '100%',
                height: 65,
                marginRight: 13,
                objectFit: 'cover',
                width: 65,
              }}
              alt="profile_image"
            />
            {isFavorite && (
              <div style={{ position: 'absolute', top: '75%', right: 0 }}>
                <Icon color={Colors.favorites_400} name="heart" />
              </div>
            )}
          </Box>

          <Column flex={1}>
            <Text
              bold
              fontSize={20}
              maxWidth={280}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {fullName}
            </Text>
            <Text>{isAvailable ? profession : 'Currently unavailable'}</Text>
          </Column>

          {isInviteSent ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 5,
              }}
            >
              <Icon name="check-sent" size={24} />

              <Text color={Colors.neutral_400} fontSize={14}>
                Sent
              </Text>
            </motion.div>
          ) : (
            <Checkbox
              checked={isSelected}
              style={{
                height: 18,
                width: 18,
              }}
            />
          )}
        </Row>
      </motion.div>
    </AnimatePresence>
  );
};
